import { FC, MouseEvent } from "react";
import {
  Avatar,
  Badge,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  styled,
  useTheme,
} from "@mui/material";
import { formatScore } from "@context/utils";
import { Challenge } from "@/models/pdfData";
import {
  Cancel,
  Pending,
  ThumbDown,
  ThumbUp,
  Warning,
} from "@mui/icons-material";
import {
  challengeComplete,
  getChallengeTypeTranslation,
} from "@/utils/challenge-utils";

const smallIconStyling = {
  transform: "translate(-12px, 0px)",
  width: 14,
  height: 14,
};

const SmallThumbUp = styled(ThumbUp)(() => smallIconStyling);
const SmallThumbDown = styled(ThumbDown)(() => smallIconStyling);
const SmallCancel = styled(Cancel)(() => smallIconStyling);
const SmallWarning = styled(Warning)(({ theme }) => ({
  ...smallIconStyling,
  color: theme.palette.warning.main,
}));
const SmallPending = styled(Pending)(() => smallIconStyling);

const ChallengeListItem: FC<{
  challenge: Challenge;
  selected: boolean;
  onClick: (challenge: Challenge) => void;
  handleContextMenu: (event: MouseEvent, challenge: Challenge) => void;
  teamIconUrls: { team1?: string; team2?: string };
}> = ({ challenge, selected, onClick, handleContextMenu, teamIconUrls }) => {
  const { palette } = useTheme();

  const isDeleted = challenge.id.startsWith("deleted");
  const isChallengeComplete = challengeComplete(challenge);

  const getScore = (challenge: Challenge) => {
    const relevantScore = challenge.scoreRallyEnd ?? challenge.scoreRallyStart;
    return formatScore(relevantScore, challenge.team);
  };

  const getIconForChallenge = (challenge: Challenge | undefined) => {
    const sx = {
      filter:
        palette.mode === "dark"
          ? "drop-shadow(0px 0px 2px black) drop-shadow(0px 0px 2px black)"
          : undefined,
    };

    if (!isChallengeComplete) {
      return <SmallWarning sx={sx} />;
    }

    if (challenge?.challengeResult === "unresolvable") {
      return <SmallCancel sx={sx} color={"warning"} />;
    }

    if (challenge?.team === "referee") {
      return (
        <img
          style={smallIconStyling}
          src={teamIconUrls[challenge.challengeResult]}
        />
      );
    }

    if (challenge?.challengeResult === challenge?.team) {
      return <SmallThumbUp sx={sx} color={"success"} />;
    } else {
      return <SmallThumbDown sx={sx} color={"error"} />;
    }
  };

  return (
    <ListItemButton
      selected={selected}
      onClick={() => !isDeleted && onClick(challenge)}
      key={`challenge-item-${challenge.id}`}
      className={isDeleted ? "Mui-disabled" : ""}
      sx={{ pointerEvents: "auto !important" }}
      onContextMenu={(event) =>
        isDeleted && handleContextMenu(event, challenge)
      }
    >
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeContent={getIconForChallenge(challenge)}
      >
        <ListItemAvatar>
          <Avatar
            src={
              challenge.team === "referee"
                ? "/VBL_Logo.png"
                : teamIconUrls[challenge.team]
            }
          />
        </ListItemAvatar>
      </Badge>

      <ListItemText
        primary={`${getScore(challenge)} (Satz ${challenge.set})`}
        secondary={getChallengeTypeTranslation(challenge.typeOfChallenge)}
        slotProps={{
          primary: {
            color: isChallengeComplete
              ? palette.text.primary
              : palette.warning.main,
          },
        }}
      />
    </ListItemButton>
  );
};

export default ChallengeListItem;
